import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import LandingLogo from './LandingLogo';
import { NAV_LINKS } from './ebContent';
import { useDemoModal } from './DemoModal';
import './landing-eb.css';
import './landing-eb-nav.css';

// Brand icons: src/assets/images/eb/icons/<name>-<green|cream|gold>.png
const iconCtx = require.context('../../assets/images/eb/icons', false, /\.png$/);
const navIcon = (name) => {
  try { return iconCtx(`./${name}-green.png`); } catch (e) { return null; }
};

// Icon for each Resources menu item, keyed by route (matches the SiteNav mockup).
const MENU_ICONS = {
  '/resources/skills-framework': 'target',
  '/resources/interaction-gallery': 'eye',
  '/resources/report-gallery': 'layout',
  '/resources/guides': 'book',
  '/resources/comparisons': 'check',
  '/resources/client-stories': 'award',
};

/**
 * Public site navbar: flat sticky bar (EthosBoard brand).
 * Logo + page links on the left (current page underlined), Contact us and Book a demo on the right.
 * Resources opens a two-column mega-menu with icons; the current item is highlighted.
 */
const LandingNavbar = () => {
  const [open, setOpen] = useState(false);          // mobile menu
  const [menu, setMenu] = useState('');             // which dropdown is open (by link label)
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const barRef = useRef(null);
  const { openDemo } = useDemoModal();

  const path = location.pathname.replace(/\/+$/, '') || '/';
  // A link is active on its own route, on sub-routes, and on any extra routes listed in `also`
  // (the home page "/" counts as the Platform page).
  const isActive = (item) => (
    path === item.to
    || path.startsWith(`${item.to}/`)
    || (item.also || []).includes(path)
  );
  // A dropdown item is active on its own route or any sub-route.
  const contactActive = path === '/enquiry';
  const isChildActive = (c) => path === c.to || path.startsWith(`${c.to}/`);

  const go = (to) => {
    setOpen(false);
    setMenu('');
    if (to === path) window.scrollTo({ top: 0, behavior: 'smooth' }); // already here: back to the top
    else navigate(to);
  };

  // close menus on navigation
  useEffect(() => {
    setOpen(false);
    setMenu('');
  }, [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // close the dropdown on outside click / Escape
  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) setMenu('');
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenu(''); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  return (
    <header ref={barRef} className={`eb eb-nav${scrolled ? ' is-scrolled' : ''}${open ? ' is-open' : ''}`}>
      <div className="eb-nav-inner">
        {/* LandingLogo renders its own link, so this wrapper must not be an <a> (nested <a> warning) */}
        <div
          className="eb-nav-brand"
          role="link"
          tabIndex={0}
          aria-label="EthosBoard home"
          onClick={() => go('/')}
          onKeyDown={(e) => { if (e.key === 'Enter') go('/'); }}
          style={{ cursor: 'pointer' }}
        >
          <LandingLogo variant="light" layout="lockup" size="md" />
        </div>

        <nav className="eb-nav-links" aria-label="Primary">
          {NAV_LINKS.map((item) => {
            const active = isActive(item);
            if (!item.children) {
              return (
                <button
                  key={item.to}
                  type="button"
                  className={`eb-nav-link${active ? ' is-active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => go(item.to)}
                >
                  {item.label}
                </button>
              );
            }
            const isOpen = menu === item.label;
            return (
              <div
                key={item.to}
                className="eb-nav-drop"
                onMouseEnter={() => setMenu(item.label)}
                onMouseLeave={() => setMenu('')}
              >
                <button
                  type="button"
                  className={`eb-nav-link${active ? ' is-active' : ''}`}
                  aria-haspopup="true"
                  aria-expanded={isOpen}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => setMenu(isOpen ? '' : item.label)}
                >
                  {item.label}
                  <FaChevronDown size={9} className="eb-nav-caret" aria-hidden="true" />
                </button>
                {isOpen && (
                  <div className="eb-nav-panel is-mega" role="menu">
                    {item.children.map((c) => {
                      const iconSrc = navIcon(c.icon || MENU_ICONS[c.to]);
                      const childActive = isChildActive(c);
                      return (
                        <button
                          key={c.to}
                          type="button"
                          role="menuitem"
                          className={`eb-nav-panel-item${childActive ? ' is-active' : ''}`}
                          aria-current={childActive ? 'page' : undefined}
                          onClick={() => go(c.to)}
                        >
                          <span className="eb-nav-panel-icon" aria-hidden="true">
                            {iconSrc && <img src={iconSrc} alt="" />}
                          </span>
                          <span className="eb-nav-panel-text">
                            <b>{c.label}</b>
                            {c.desc && <span>{c.desc}</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="eb-nav-actions">
          <button
            type="button"
            className={`eb-nav-signin${contactActive ? ' is-active' : ''}`}
            aria-current={contactActive ? 'page' : undefined}
            onClick={() => go('/enquiry')}
          >
            Contact us
          </button>
          <button type="button" className="eb-nav-cta" onClick={() => { setOpen(false); openDemo(); }}>
            Book a demo
          </button>
          <button
            type="button"
            className="eb-nav-toggle"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <FaTimes size={18} /> : <FaBars size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="eb-nav-menu" aria-label="Mobile">
          {NAV_LINKS.map((item) => (
            <React.Fragment key={item.to}>
              <button
                type="button"
                className={`eb-nav-menu-link${isActive(item) ? ' is-active' : ''}`}
                aria-current={isActive(item) ? 'page' : undefined}
                onClick={() => go(item.to)}
              >
                {item.label}
              </button>
              {item.children && item.children.map((c) => (
                <button
                  key={c.to}
                  type="button"
                  className={`eb-nav-menu-link is-sub${isChildActive(c) ? ' is-active' : ''}`}
                  aria-current={isChildActive(c) ? 'page' : undefined}
                  onClick={() => go(c.to)}
                >
                  {c.label}
                </button>
              ))}
            </React.Fragment>
          ))}
          <button
            type="button"
            className={`eb-nav-menu-link${contactActive ? ' is-active' : ''}`}
            aria-current={contactActive ? 'page' : undefined}
            onClick={() => go('/enquiry')}
          >
            Contact us
          </button>
        </nav>
      )}
    </header>
  );
};

export default LandingNavbar;
